// Covenant Calibration (capture-only) — card 3 of 3 on the loan Profile
// tab. DSCR / DACA banking accounts: one row to start, "+ Add account" for
// more. Per row: institution, account type, account number (masked last-4
// from the server; the full number is sent only when the staffer types a
// new one), purpose, DACA executed + date (date shown only when executed),
// and a reserve-account flag. Capture only — no calculations.
//
// Edit is an inline editor over a working copy of accounts[]. Save diffs the
// working copy against the loaded rows: new rows (no id) → addAccount,
// changed existing rows → updateAccount, removed rows → removeAccount; each
// mutation refetches the record.

import { useEffect, useState } from "react";
import {
  Button,
  Card,
  FormField,
  Input,
  SkeletonList,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatCalendarDate } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import {
  useAddCovenantBankAccount,
  useConfirmBanking,
  useCovenantCalibration,
  useRemoveCovenantBankAccount,
  useUpdateCovenantBankAccount,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  DSCR_BANK_ACCOUNT_PURPOSE_LABEL,
  DSCR_BANK_ACCOUNT_TYPE_LABEL,
  type CovenantBankAccountDto,
  type DscrBankAccountPurpose,
  type DscrBankAccountType,
  type UpsertBankAccountRequest,
} from "@/api/portfolioAdmin/types";
import { CovenantConfirmRow } from "./CovenantConfirmRow";

const TYPES = Object.keys(DSCR_BANK_ACCOUNT_TYPE_LABEL) as DscrBankAccountType[];
const PURPOSES = Object.keys(
  DSCR_BANK_ACCOUNT_PURPOSE_LABEL,
) as DscrBankAccountPurpose[];

/** Editor working-copy row. `id` null = a new account not yet persisted.
 *  `accountNumberInput` is always blank on load — for an existing row a
 *  blank value means "keep the stored number" (send null); a typed value
 *  is the new full number. */
interface DraftRow {
  /** Stable client key so React reconciles rows across edits. */
  key: string;
  id: string | null;
  institutionName: string;
  accountType: DscrBankAccountType;
  accountNumberMasked: string | null;
  accountNumberInput: string;
  purpose: DscrBankAccountPurpose;
  dacaExecuted: boolean;
  dacaDate: string; // YYYY-MM-DD, "" when none
  isReserveAccount: boolean;
}

let newRowSeq = 0;

function toDraft(a: CovenantBankAccountDto): DraftRow {
  return {
    key: a.id,
    id: a.id,
    institutionName: a.institutionName,
    accountType: a.accountType,
    accountNumberMasked: a.accountNumberMasked,
    accountNumberInput: "",
    purpose: a.purpose,
    dacaDate: a.dacaDate ? a.dacaDate.slice(0, 10) : "",
    dacaExecuted: a.dacaExecuted,
    isReserveAccount: a.isReserveAccount,
  };
}

function blankDraft(): DraftRow {
  return {
    key: `new-${newRowSeq++}`,
    id: null,
    institutionName: "",
    accountType: "Operating",
    accountNumberMasked: null,
    accountNumberInput: "",
    purpose: "DscrMonitoring",
    dacaExecuted: false,
    dacaDate: "",
    isReserveAccount: false,
  };
}

function toRequest(row: DraftRow): UpsertBankAccountRequest {
  const typed = row.accountNumberInput.trim();
  return {
    institutionName: row.institutionName.trim(),
    accountType: row.accountType,
    // null = keep the existing stored number (existing rows); a new row with
    // a blank input also sends null (nothing entered yet).
    accountNumber: typed === "" ? null : typed,
    purpose: row.purpose,
    dacaExecuted: row.dacaExecuted,
    // The server binds this to a DateOnly?, which parses a bare YYYY-MM-DD
    // (the `<input type="date">` value) — never a datetime.
    dacaDate: row.dacaExecuted && row.dacaDate ? row.dacaDate : null,
    isReserveAccount: row.isReserveAccount,
  };
}

export function DscrDacaBankingCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const canEdit = auth.hasPermission("Loans.Manage") || auth.hasRoleCode("ADMIN");
  const toast = useToast();
  const query = useCovenantCalibration(loanId);
  const add = useAddCovenantBankAccount(loanId);
  const updateAccount = useUpdateCovenantBankAccount(loanId);
  const removeAccount = useRemoveCovenantBankAccount(loanId);
  const confirm = useConfirmBanking(loanId);

  const data = query.data;
  const accounts = data?.accounts ?? [];

  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the working copy when an edit starts. Start a brand-new (empty)
  // account-set with one blank row to capture.
  useEffect(() => {
    if (!editing) return;
    setRows(accounts.length > 0 ? accounts.map(toDraft) : [blankDraft()]);
    setRemovedIds([]);
  }, [editing, accounts]);

  const startEdit = () => {
    setError(null);
    setEditing(true);
  };

  const patch = (key: string, change: Partial<DraftRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...change } : r)));

  const removeRow = (row: DraftRow) => {
    if (row.id) setRemovedIds((ids) => [...ids, row.id!]);
    setRows((rs) => rs.filter((r) => r.key !== row.key));
  };

  const saving = add.isPending || updateAccount.isPending || removeAccount.isPending;

  const save = async () => {
    setError(null);
    // Validate: institution required on every row we'll persist.
    const missing = rows.find((r) => r.institutionName.trim() === "");
    if (missing) {
      setError("Every account needs an institution name.");
      return;
    }
    try {
      for (const id of removedIds) {
        await removeAccount.mutateAsync(id);
      }
      for (const row of rows) {
        const body = toRequest(row);
        if (row.id) await updateAccount.mutateAsync({ accountId: row.id, body });
        else await add.mutateAsync(body);
      }
      toast.success("Banking accounts saved");
      setEditing(false);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const onConfirm = async () => {
    try {
      await confirm.mutateAsync();
      toast.success("Banking confirmed");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card data-testid="covenant-banking-card">
      <div className="mb-space-3 flex items-start justify-between gap-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            DSCR / DACA Banking
          </div>
          <p className="text-caption text-gray-mid m-0">
            Monitored accounts &amp; control agreements — capture only.
          </p>
        </div>
        {canEdit && !editing && !query.isLoading && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={startEdit}
            data-testid="covenant-banking-edit"
          >
            Edit
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <SkeletonList rows={2} data-testid="covenant-banking-loading" />
      ) : query.isError || !data ? (
        <p className="text-small text-status-error m-0" role="alert">
          Couldn&apos;t load DSCR / DACA banking. Please retry.
        </p>
      ) : !editing ? (
        <>
          {accounts.length === 0 ? (
            <p className="text-small text-gray-mid m-0" data-testid="covenant-banking-empty">
              No bank accounts recorded yet.
            </p>
          ) : (
            <ul className="flex flex-col gap-space-3 m-0 p-0 list-none">
              {accounts.map((a) => (
                <li
                  key={a.id}
                  className="border border-border-light rounded-md p-space-3"
                  data-testid={`covenant-account-${a.id}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2 text-small">
                    <Value label="Institution">{a.institutionName || "—"}</Value>
                    <Value label="Account type">
                      {DSCR_BANK_ACCOUNT_TYPE_LABEL[a.accountType]}
                    </Value>
                    <Value label="Account number">{a.accountNumberMasked || "—"}</Value>
                    <Value label="Purpose">
                      {DSCR_BANK_ACCOUNT_PURPOSE_LABEL[a.purpose]}
                    </Value>
                    <Value label="DACA executed">
                      {a.dacaExecuted
                        ? `Yes${a.dacaDate ? ` · ${formatCalendarDate(a.dacaDate)}` : ""}`
                        : "No"}
                    </Value>
                    <Value label="Reserve account">
                      {a.isReserveAccount ? "Yes" : "No"}
                    </Value>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <CovenantConfirmRow
            confirmation={data.bankingConfirmation}
            canEdit={canEdit}
            editing={editing}
            onConfirm={() => void onConfirm()}
            confirming={confirm.isPending}
            testIdPrefix="covenant-banking"
          />
        </>
      ) : (
        <div data-testid="covenant-banking-form">
          <ul className="flex flex-col gap-space-3 m-0 p-0 list-none">
            {rows.map((row) => (
              <li
                key={row.key}
                className="border border-border-light rounded-md p-space-3"
                data-testid={`covenant-account-editor-${row.key}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
                  <FormField label="Institution name">
                    <Input
                      aria-label="Institution name"
                      value={row.institutionName}
                      onChange={(e) => patch(row.key, { institutionName: e.target.value })}
                      data-testid={`covenant-account-institution-${row.key}`}
                    />
                  </FormField>
                  <FormField label="Account type">
                    <Select
                      aria-label="Account type"
                      value={row.accountType}
                      onChange={(e) =>
                        patch(row.key, {
                          accountType: e.target.value as DscrBankAccountType,
                        })
                      }
                    >
                      {TYPES.map((v) => (
                        <option key={v} value={v}>
                          {DSCR_BANK_ACCOUNT_TYPE_LABEL[v]}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                  <FormField
                    label="Account number"
                    helperText={
                      row.id
                        ? row.accountNumberMasked
                          ? `On file: ${row.accountNumberMasked}. Leave blank to keep it.`
                          : "Leave blank to keep the existing number."
                        : undefined
                    }
                  >
                    <Input
                      aria-label="Account number"
                      placeholder={row.accountNumberMasked ?? ""}
                      value={row.accountNumberInput}
                      onChange={(e) =>
                        patch(row.key, { accountNumberInput: e.target.value })
                      }
                      data-testid={`covenant-account-number-${row.key}`}
                    />
                  </FormField>
                  <FormField label="Purpose">
                    <Select
                      aria-label="Purpose"
                      value={row.purpose}
                      onChange={(e) =>
                        patch(row.key, {
                          purpose: e.target.value as DscrBankAccountPurpose,
                        })
                      }
                    >
                      {PURPOSES.map((v) => (
                        <option key={v} value={v}>
                          {DSCR_BANK_ACCOUNT_PURPOSE_LABEL[v]}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                  <FormField label="DACA executed">
                    <Select
                      aria-label="DACA executed"
                      value={row.dacaExecuted ? "yes" : "no"}
                      onChange={(e) =>
                        patch(row.key, { dacaExecuted: e.target.value === "yes" })
                      }
                      data-testid={`covenant-account-daca-${row.key}`}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </Select>
                  </FormField>
                  {row.dacaExecuted && (
                    <FormField label="DACA date">
                      <Input
                        type="date"
                        aria-label="DACA date"
                        value={row.dacaDate}
                        onChange={(e) => patch(row.key, { dacaDate: e.target.value })}
                        data-testid={`covenant-account-daca-date-${row.key}`}
                      />
                    </FormField>
                  )}
                </div>
                <div className="mt-space-2 flex items-center justify-between gap-space-3">
                  <label className="flex items-center gap-space-2 text-small text-onyx">
                    <input
                      type="checkbox"
                      checked={row.isReserveAccount}
                      onChange={(e) =>
                        patch(row.key, { isReserveAccount: e.target.checked })
                      }
                      aria-label="Reserve account"
                      data-testid={`covenant-account-reserve-${row.key}`}
                    />
                    Reserve account
                  </label>
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => removeRow(row)}
                    data-testid={`covenant-account-remove-${row.key}`}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-space-3">
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setRows((rs) => [...rs, blankDraft()])}
              data-testid="covenant-banking-add"
            >
              + Add account
            </Button>
          </div>

          {error && (
            <p
              className="text-caption text-status-error mt-space-3 m-0"
              role="alert"
              data-testid="covenant-banking-error"
            >
              {error}
            </p>
          )}

          <div className="mt-space-3 flex justify-end gap-space-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void save()}
              loading={saving}
              data-testid="covenant-banking-save"
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Value({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-onyx mt-[2px]">{children}</div>
    </div>
  );
}
